/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 3 Nov 2026 · hours or man-days — the switch, and every `.days` sibling.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `decisions.md` DU-1. Spread into `HU` and `EN` in `./catalogue.ts`.
 *
 * ⚠️ **A `<key>.days` entry is what `t('<key>')` answers in man-days mode** (`i18n/index.tsx`), so a
 * header or a sentence that names the unit flips with the figures. Every base key listed in
 * `DURATION_KEYS` must have one in both languages — `__tests__/duration-keys.test.ts` holds it.
 *
 * ⚠️ **A figure rendered by `useDuration().fmt` already carries `nap` / `d`**, so a template whose
 * hours version writes the unit after a placeholder (`{hours} óra`) drops it in its `.days` sibling
 * rather than translating it — `{hours}` is `1,5 nap` there.
 *
 * ⚠️ **Only durations.** *Túlóra*, *óradíj*, *órarend*, *munkaóra-keret* as a name, an hourly rate —
 * none of these are an amount of time somebody reads, and none of them change.
 */
export const HU = {
  'nav.durationDays': 'Embernapban',
  'account.prefs.durationUnit': 'Időtartam',
  'account.prefs.durationUnit.optionHours': 'Órában (7:30)',
  'account.prefs.durationUnit.optionDays': 'Embernapban (1 nap = 8 óra)',
  'account.prefs.durationUnit.note':
    'Minden képernyő, export, e-mail és az Agent is így mutatja az időt, és minden időtartammező így indul — a mező melletti választóval az adott mezőt átállíthatod.',
  'duration.unit.label': 'Mértékegység',
  'duration.unit.hours': 'óra',
  'duration.unit.days': 'nap',
  'lineItem.field.perPeriod': '/ időszak',

  /* ── the `.days` siblings: headers, sentences and input help that name the unit ── */
  'common.hours.days': 'nap',
  'lineItem.error.estimate.days': 'Az embernapot nem sikerült értelmezni.',
  'finance.breakdown.col.logged.days': 'Tény embernap',
  'finance.tip.bd.logged.name.days': 'Tény embernap',
  'finance.tip.bd.planned.name.days': 'Tervezett embernap',
  'effort.tip.billable.name.days': 'Elszámolható embernap',
  'dash.row.frame.days': '{hours}/hét',
  'resource.cell.hours.days': '{name} embernapjai ebben az oszlopban',
  'time.column.hours.days': 'Embernap',
  'projects.column.hours.days': 'Embernap / becslés',
  'time.error.dayFull.days': 'Ezzel a nap összesen {total} lenne, a napi maximum viszont {max}.',
  'time.hours.help.days':
    'Elfogad 1, 1,5, 0,25 vagy 1n alakot (1 nap = 8 óra); a 4h vagy 4:00 órát jelent.',
  'time.error.hours.days': 'Nagyobbnak kell lennie 0-nál. Elfogad 1, 1,5, 0,25 vagy 1n alakot.',
  'task.field.estimateHint.days': 'Elfogad 1, 1,5, 0,25 vagy 1n alakot (1 nap = 8 óra).',
  'task.error.estimate.days':
    'Ezt nem tudom időként értelmezni. Elfogad 1, 1,5, 0,25 vagy 1n alakot.',
  'logs.entry.badDuration.days': 'Add meg az időtartamot, például 1 vagy 1,5 (embernap).',
  'time.log.hours.days': 'Embernap',
  'history.field.hours.days': 'Embernap',
  'history.field.weeklyHours.days': 'Heti embernap',
  'people.column.month.days': 'E havi embernap',
  'people.field.scheduleFrom.days': '{dur} / hét · kezdet: {from}',
  'settings.schedules.weekTotal.days': '{dur} / hét',
  'settings.schedules.weekly.days': 'Heti keret (embernap)',
  'settings.schedules.days.days': 'Napi embernap',
  'settings.schedules.fixHint.days': 'Pontos embernap minden napra',
  'onboarding.sched.weekly.days': 'Heti embernap',
  'onboarding.sched.days.days': 'Napi embernap',
  'leave.column.hoursTimes.days': 'Embernap × szorzó',
  'report.col.hours.days': 'Embernap',
  'report.col.taskLogged.days': 'Rögzített embernap',
  'report.col.taskRemaining.days': 'Hátralévő embernap',
  'report.column.hours.days': 'Rögzített embernap',
  'report.metric.hours.days': 'Rögzített embernap összege',
  'report.metric.expected.days': 'Elvárt embernap',
  'report.metric.missing.days': 'Hiányzó embernap',
  'report.metric.leave.days': 'Szabadság (embernap)',
  'deals.column.hours.days': 'Terv embernap',
  'dealDemand.raw.days': 'Nyers embernap',
  'dealOverview.rail.allocated.days': 'Σ allokált embernap',
  'dealResource.tile.allocated.days': 'Σ allokált embernap',
  'dealResource.grid.label.days': 'Heti embernapok sortételenként és placeholderenként',
  'dealResource.error.hours.days':
    'Egy cella nem embernap: írd 1, 1,5 vagy 0,25 alakban (órában: 4h).',
  'dealResource.error.max.days': 'Egy cellában egy hétre legfeljebb 50 embernap lehet.',
  'dealResource.confirm.deleteBody.days':
    'A(z) „{name}” placeholder és minden heti embernapja törlődik a dealről, minden sortételről.',
  'dealPlan.field.estimate.days': 'Becslés (embernap)',
  'dealPlan.error.hours.days': 'A becslést embernapban add meg: 1, 1,5 vagy 0,25 (órában: 4h).',
  'dealScenario.tile.hours.days': 'Összes embernap',
  'dealScenario.foot.hours.days': 'Σ embernap',
  'dealScenario.view.labelWith.days': 'Embernap · opciókkal',
  'dealScenario.view.labelWithout.days': 'Embernap · opciók nélkül',
  'dealScenario.grid.hint.days': 'A cellákba fejenkénti embernapot írsz, Enterrel mentesz.',
  'dealScenario.error.hours.days': 'Az embernapot így add meg: 1, 1,5 vagy 0,25 (órában: 4h).',
  'dealScenario.error.maxHours.days': 'Egy cellába legfeljebb 250 embernap kerülhet.',
};

export const EN: Record<keyof typeof HU, string> = {
  'nav.durationDays': 'Man-days',
  'account.prefs.durationUnit': 'Durations',
  'account.prefs.durationUnit.optionHours': 'In hours (7:30)',
  'account.prefs.durationUnit.optionDays': 'In man-days (1 d = 8 h)',
  'account.prefs.durationUnit.note':
    'Every screen, export, email and the Agent show time this way, and every duration field starts in it — the picker beside a field switches that one.',
  'duration.unit.label': 'Unit',
  'duration.unit.hours': 'hours',
  'duration.unit.days': 'days',
  'lineItem.field.perPeriod': '/ period',

  'common.hours.days': 'man-days',
  'lineItem.error.estimate.days': 'That is not a number of man-days we can read.',
  'finance.breakdown.col.logged.days': 'Logged',
  'finance.tip.bd.logged.name.days': 'Logged man-days',
  'finance.tip.bd.planned.name.days': 'Planned man-days',
  'effort.tip.billable.name.days': 'Billable man-days',
  'dash.row.frame.days': '{hours}/week',
  'resource.cell.hours.days': '{name} man-days in this column',
  'time.column.hours.days': 'Man-days',
  'projects.column.hours.days': 'Man-days / estimate',
  'time.error.dayFull.days': 'That would take the day to {total}, and the daily maximum is {max}.',
  'time.hours.help.days': 'Accepts 1, 1.5, 0.25 or 1d (1 d = 8 h); 4h or 4:00 means hours.',
  'time.error.hours.days': 'It has to be greater than 0. Accepts 1, 1.5, 0.25 or 1d.',
  'task.field.estimateHint.days': 'Accepts 1, 1.5, 0.25 or 1d (1 d = 8 h).',
  'task.error.estimate.days': 'That is not an amount of time. Accepts 1, 1.5, 0.25 or 1d.',
  'logs.entry.badDuration.days': 'Give the duration, for example 1 or 1.5 (man-days).',
  'time.log.hours.days': 'Man-days',
  'history.field.hours.days': 'Man-days',
  'history.field.weeklyHours.days': 'Weekly man-days',
  'people.column.month.days': 'Man-days this month',
  'people.field.scheduleFrom.days': '{dur} / week · from {from}',
  'settings.schedules.weekTotal.days': '{dur} / week',
  'settings.schedules.weekly.days': 'Weekly frame (man-days)',
  'settings.schedules.days.days': 'Man-days per day',
  'settings.schedules.fixHint.days': 'Exact man-days for every day',
  'onboarding.sched.weekly.days': 'Weekly man-days',
  'onboarding.sched.days.days': 'Man-days per day',
  'leave.column.hoursTimes.days': 'Man-days × rate',
  'report.col.hours.days': 'Man-days',
  'report.col.taskLogged.days': 'Logged man-days',
  'report.col.taskRemaining.days': 'Remaining man-days',
  'report.column.hours.days': 'Tracked man-days',
  'report.metric.hours.days': 'Sum of tracked man-days',
  'report.metric.expected.days': 'Expected man-days',
  'report.metric.missing.days': 'Missing man-days',
  'report.metric.leave.days': 'Leave man-days',
  'deals.column.hours.days': 'Planned man-days',
  'dealDemand.raw.days': 'Raw man-days',
  'dealOverview.rail.allocated.days': 'Σ allocated man-days',
  'dealResource.tile.allocated.days': 'Σ allocated man-days',
  'dealResource.grid.label.days': 'Weekly man-days by line item and placeholder',
  'dealResource.error.hours.days':
    'A cell is not man-days: write it as 1, 1.5 or 0.25 (hours as 4h).',
  'dealResource.error.max.days': 'A cell can hold at most 50 man-days for a week.',
  'dealResource.confirm.deleteBody.days':
    'The placeholder “{name}” and all its weekly man-days are deleted from the deal, on every line item.',
  'dealPlan.field.estimate.days': 'Estimate (man-days)',
  'dealPlan.error.hours.days': 'Enter the estimate in man-days: 1, 1.5 or 0.25 (hours as 4h).',
  'dealScenario.tile.hours.days': 'Total man-days',
  'dealScenario.foot.hours.days': 'Σ man-days',
  'dealScenario.view.labelWith.days': 'Man-days · with options',
  'dealScenario.view.labelWithout.days': 'Man-days · without options',
  'dealScenario.grid.hint.days': 'Type man-days per head into the cells; Enter saves.',
  'dealScenario.error.hours.days': 'Type man-days as 1, 1.5 or 0.25 (hours as 4h).',
  'dealScenario.error.maxHours.days': 'A cell holds at most 250 man-days.',
};

/**
 * The base keys whose words name the unit and so carry a `.days` sibling above. ⚠️ Maintained by
 * hand, and the test fails when a listed key has no sibling or a sibling has no listed base.
 */
export const DURATION_KEYS: readonly string[] = [
  'common.hours',
  'lineItem.error.estimate',
  'finance.breakdown.col.logged',
  'finance.tip.bd.logged.name',
  'finance.tip.bd.planned.name',
  'effort.tip.billable.name',
  'dash.row.frame',
  'resource.cell.hours',
  'time.column.hours',
  'projects.column.hours',
  'time.error.dayFull',
  'time.hours.help',
  'time.error.hours',
  'task.field.estimateHint',
  'task.error.estimate',
  'logs.entry.badDuration',
  'time.log.hours',
  'history.field.hours',
  'history.field.weeklyHours',
  'people.column.month',
  'people.field.scheduleFrom',
  'settings.schedules.weekTotal',
  'settings.schedules.weekly',
  'settings.schedules.days',
  'settings.schedules.fixHint',
  'onboarding.sched.weekly',
  'onboarding.sched.days',
  'leave.column.hoursTimes',
  'report.col.hours',
  'report.col.taskLogged',
  'report.col.taskRemaining',
  'report.column.hours',
  'report.metric.hours',
  'report.metric.expected',
  'report.metric.missing',
  'report.metric.leave',
  'deals.column.hours',
  'dealDemand.raw',
  'dealOverview.rail.allocated',
  'dealResource.tile.allocated',
  'dealResource.grid.label',
  'dealResource.error.hours',
  'dealResource.error.max',
  'dealResource.confirm.deleteBody',
  'dealPlan.field.estimate',
  'dealPlan.error.hours',
  'dealScenario.tile.hours',
  'dealScenario.foot.hours',
  'dealScenario.view.labelWith',
  'dealScenario.view.labelWithout',
  'dealScenario.grid.hint',
  'dealScenario.error.hours',
  'dealScenario.error.maxHours',
];
